import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, User } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";

const PandaLogo = () => (
  <span className="font-display text-3xl font-bold lowercase tracking-tight text-brand-ink">
    panda tata
  </span>
);

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { t, lang, changeLang } = useLang();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const links = [
    { to: "/", label: t("nav_home") },
    { to: "/story", label: t("nav_story") },
    { to: "/products", label: t("nav_products") },
    { to: "/franchise", label: t("nav_franchise") },
    { to: "/news", label: t("nav_news") },
    { to: "/contact", label: t("nav_contact") },
  ];

  const linkClass = ({ isActive }) =>
    `text-sm font-semibold transition-colors hover:text-brand-greendark ${
      isActive ? "text-brand-greendark" : "text-brand-ink"
    }`;

  return (
    <header className="sticky top-0 z-50 border-b-2 border-brand-ink bg-brand-cream/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3">
        <Link to="/" data-testid="nav-logo" onClick={() => setOpen(false)}>
          <PandaLogo />
        </Link>

        <nav className="hidden items-center gap-7 lg:flex">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}`} className={linkClass} end={l.to === "/"}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <div className="hidden items-center rounded-full border-2 border-brand-ink text-xs font-bold sm:flex">
            <button
              data-testid="lang-en"
              onClick={() => changeLang("en")}
              className={`rounded-full px-3 py-1 ${lang === "en" ? "bg-brand-ink text-brand-yellow" : "text-brand-ink"}`}
            >EN</button>
            <button
              data-testid="lang-ms"
              onClick={() => changeLang("ms")}
              className={`rounded-full px-3 py-1 ${lang === "ms" ? "bg-brand-ink text-brand-yellow" : "text-brand-ink"}`}
            >BM</button>
          </div>

          {user ? (
            <div className="hidden items-center gap-2 lg:flex">
              {user.role === "admin" ? (
                <Link to="/admin" data-testid="nav-admin" className="rounded-full border-2 border-brand-ink px-4 py-2 text-sm font-bold hover-brutal bg-white">
                  {t("nav_admin")}
                </Link>
              ) : (
                <Link to="/member" data-testid="nav-member" className="flex items-center gap-1 rounded-full border-2 border-brand-ink bg-brand-green px-4 py-2 text-sm font-bold hover-brutal">
                  <User className="h-4 w-4" /> {t("nav_member")}
                </Link>
              )}
              <button data-testid="nav-logout" onClick={() => { logout(); navigate("/"); }} className="text-sm font-semibold text-brand-ink hover:text-brand-greendark">
                {t("nav_logout")}
              </button>
            </div>
          ) : (
            <div className="hidden items-center gap-2 lg:flex">
              <Link to="/login" data-testid="nav-login" className="text-sm font-bold text-brand-ink hover:text-brand-greendark">
                {t("nav_login")}
              </Link>
              <Link to="/register" data-testid="nav-join" className="rounded-full border-2 border-brand-ink bg-brand-yellow px-4 py-2 text-sm font-bold hover-brutal">
                {t("nav_join")}
              </Link>
            </div>
          )}

          <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} className="grid h-10 w-10 place-items-center rounded-full border-2 border-brand-ink lg:hidden">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t-2 border-brand-ink bg-brand-cream px-5 py-4 lg:hidden">
          <div className="flex flex-col gap-3">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${l.to === "/" ? "home" : l.to.slice(1)}`} className={linkClass} end={l.to === "/"}>
                {l.label}
              </NavLink>
            ))}
            <div className="h-px bg-brand-ink/20" />
            <div className="flex w-fit items-center rounded-full border-2 border-brand-ink text-xs font-bold">
              <button data-testid="mobile-lang-en" onClick={() => changeLang("en")}
                className={`rounded-full px-3 py-1 ${lang === "en" ? "bg-brand-ink text-brand-yellow" : "text-brand-ink"}`}>EN</button>
              <button data-testid="mobile-lang-ms" onClick={() => changeLang("ms")}
                className={`rounded-full px-3 py-1 ${lang === "ms" ? "bg-brand-ink text-brand-yellow" : "text-brand-ink"}`}>BM</button>
            </div>
            <div className="h-px bg-brand-ink/20" />
            {user ? (
              <>
                <Link to={user.role === "admin" ? "/admin" : "/member"} onClick={() => setOpen(false)} data-testid={user.role === "admin" ? "mobile-nav-admin" : "mobile-nav-member"} className="font-bold text-brand-greendark">
                  {user.role === "admin" ? t("nav_admin") : t("nav_member")}
                </Link>
                <button onClick={() => { logout(); setOpen(false); navigate("/"); }} data-testid="mobile-nav-logout" className="text-left font-semibold">{t("nav_logout")}</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} data-testid="mobile-nav-login" className="font-bold">{t("nav_login")}</Link>
                <Link to="/register" onClick={() => setOpen(false)} data-testid="mobile-nav-join" className="font-bold text-brand-greendark">{t("nav_join")}</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
